import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  withStyles,
  IconButton,
  Collapse,
  Box,
  TablePagination
} from '@material-ui/core';
import { required, TextInput, useInput } from 'react-admin';
import React, { useCallback, useMemo, useState } from 'react';
import { AutoSizer, List as VirtualizedList } from 'react-virtualized';
import { memoize } from 'lodash';

import {
  fieldLengthValidator,
  isFieldContainsOnlySpaces
} from '../../core/utils';
import FundInvestorTagApi from '../../core/apis/FundInvestorTagApi';
import { ChevronDownIcon } from '../../core/themes/icons/ChevronDownIcon';
import { ChevronRightIcon } from '../../core/themes/icons/ChevronRightIcon';

import AddFundsDialog from './AddFundsDialog';
import { useStyles } from './utils/styles';
import { Field } from 'react-final-form';
import CheckBoxField from '../../core/components/CheckBoxField';

const styles = () => ({
  gridLarge: {
    width: '100%'
  },
  containerFlex: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    padding: '8px',
    margin: 0
  },
  checkBoxFlex: {
    display: 'flex',
    alignSelf: 'end',
    marginLeft: '20px',
    marginBottom: '11px'
  },
  buttonFlex: {
    display: 'flex',
    alignSelf: 'end',
    marginBottom: '8px'
  },
  borderLeft: {
    borderLeft: '1px solid #E0E0E0'
  },
  tableWrapper: {
    width: '100%',
    maxHeight: 'calc(100vh - 364px)',
    overflowY: 'scroll',
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px'
    },
    /* Track */
    '&::-webkit-scrollbar-track': {
      background: '#ebebeb',
      borderRadius: '10px',
      margin: '2px'
    },
    /* Handle */
    '&::-webkit-scrollbar-thumb': {
      background: '#9E9E9E',
      borderRadius: '10px'
    },
    /* Handle on hover */
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#7b7a7a'
    }
  },
  expandAllCell: {
    cursor: 'pointer',
    marginLeft: '-10px'
  }
});

const classChevron = {
  chevron: {
    fontSize: '14px',
    color: '#757575',
    transform: 'scale(0.7)'
  }
};

const FundInvestorSelection = props => {
  const {
    classes,
    fundId,
    fundName,
    investors,
    formValuesIds,
    expandAll,
    handleChecked,
    hasEditPermission
  } = props;

  const stylesSelection = useStyles();
  const [expandInvestors, setExpandInvestors] = useState(false);

  const totalInvRows = investors.length;
  const calcHeight = totalInvRows < 10 ? `${totalInvRows * 30}px` : `${300}px`;
  const selectedInvestorRows = formValuesIds.filter(id =>
    id.startsWith(`${fundId}:`)
  ).length;

  function investorRenderer({ _key, index, style }) {
    const id = `${fundId}:${investors[index].investorId}`;
    return (
      <TableRow key={id} style={style} className={stylesSelection.collapseRow}>
        <TableCell>{investors[index].investorName}</TableCell>
        <TableCell
          className={totalInvRows > 10 ? stylesSelection.cellRightScroll : ''}
        >
          <Checkbox
            disabled={!hasEditPermission}
            id={id}
            value={id}
            checked={formValuesIds.includes(id)}
            onChange={(_event, isChecked) => handleChecked([id], isChecked)}
          />
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {expandAll && (
        <TableRow className={stylesSelection.mainRow}>
          <TableCell>
            <IconButton
              size="small"
              aria-label="expand rowFund"
              onClick={() => setExpandInvestors(!expandInvestors)}
            >
              {expandInvestors ? (
                <ChevronDownIcon style={classChevron.chevron} />
              ) : (
                <ChevronRightIcon style={classChevron.chevron} />
              )}
            </IconButton>
            {fundName}
          </TableCell>
          <TableCell className={classes.borderLeft}>
            <Checkbox
              disabled={!hasEditPermission}
              onChange={(_event, isChecked) =>
                handleChecked(
                  investors.map(choice => `${fundId}:${choice.investorId}`),
                  isChecked
                )
              }
              checked={
                selectedInvestorRows > 0 &&
                totalInvRows === selectedInvestorRows
              }
              indeterminate={
                selectedInvestorRows > 0 && totalInvRows > selectedInvestorRows
              }
            />
          </TableCell>
        </TableRow>
      )}
      {expandAll && expandInvestors && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            <Collapse in={expandInvestors} timeout="auto" unmountOnExit>
              <Box margin={1}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Investors</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody style={{ height: calcHeight }}>
                    <AutoSizer>
                      {({ height, width }) => (
                        <VirtualizedList
                          rowHeight={30}
                          height={height}
                          width={width}
                          rowCount={investors.length}
                          rowRenderer={investorRenderer}
                        />
                      )}
                    </AutoSizer>
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const RenderFunds = props => {
  const {
    fundTagName,
    classes,
    source,
    resource,
    fundInvestors,
    mapFundInvSorted,
    numberOfTotalRows,
    hasEditPermission
  } = props;

  const {
    input: { onChange: finalFormOnChange, onBlur: finalFormOnBlur, value }
  } = useInput({
    resource,
    source
  });

  const [expandAll, setExpandAll] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);

  const investorSelectedRows = useMemo(() => value || [], [value]);

  const rowsCount = mapFundInvSorted.length;

  const handleChangePage = (_event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExpandAll = () => {
    setExpandAll(!expandAll);
  };

  const handleChecked = useCallback(
    (newValues, isChecked) => {
      if (isChecked) {
        const filteredNewValues = newValues.filter(val => !value.includes(val));
        finalFormOnChange([...(value || []), ...filteredNewValues]);
      } else {
        finalFormOnChange(value.filter(v => !newValues.includes(v)));
      }
      finalFormOnBlur(); // HACK: See https://github.com/final-form/react-final-form/issues/365#issuecomment-515045503
    },
    [finalFormOnChange, finalFormOnBlur, value]
  );

  const handleAllChecked = useCallback(
    (_event, isChecked) => {
      if (isChecked) {
        finalFormOnChange(
          fundInvestors.map(choice => `${choice.fundId}:${choice.investorId}`)
        );
      } else {
        finalFormOnChange([]);
      }
      finalFormOnBlur(); // HACK: See https://github.com/final-form/react-final-form/issues/365#issuecomment-515045503
    },
    [finalFormOnChange, finalFormOnBlur, fundInvestors]
  );

  return (
    <>
      <div className={classes.tableWrapper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Funds</TableCell>
              <TableCell>{fundTagName}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                <IconButton
                  size="small"
                  aria-label="expand rowAll"
                  onClick={handleExpandAll}
                  classes={{ root: classes.expandAllCell }}
                >
                  {expandAll ? (
                    <ChevronDownIcon style={classChevron.chevron} />
                  ) : (
                    <ChevronRightIcon style={classChevron.chevron} />
                  )}
                </IconButton>
                All
              </TableCell>
              <TableCell classes={{ root: classes.borderLeft }}>
                <Checkbox
                  id="SelectAll"
                  disabled={!hasEditPermission}
                  onChange={handleAllChecked}
                  checked={
                    investorSelectedRows.length > 0 &&
                    numberOfTotalRows === investorSelectedRows.length
                  }
                  indeterminate={
                    investorSelectedRows.length > 0 &&
                    numberOfTotalRows > investorSelectedRows.length
                  }
                />
              </TableCell>
            </TableRow>
            {mapFundInvSorted
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map(([fundId, { fundName, investors }]) => (
                <FundInvestorSelection
                  key={fundId}
                  classes={classes}
                  fundId={fundId}
                  fundName={fundName}
                  investors={investors}
                  formValuesIds={investorSelectedRows}
                  expandAll={expandAll}
                  handleChecked={handleChecked}
                  hasEditPermission={hasEditPermission}
                />
              ))}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        component="div"
        count={rowsCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 100]}
      />
    </>
  );
};

const RenderFundInvestors = props => {
  const {
    fundTagName,
    classes,
    fundInvestors,
    mapFundInvSorted,
    numberOfTotalRows,
    hasEditPermission
  } = props;

  if (mapFundInvSorted.length === 0) {
    return (
      <div className={classes.tableWrapper}>
        <Table style={{ minHeight: '500px' }}>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: '90%' }}>Funds</TableCell>
              <TableCell>{fundTagName}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell colSpan="2" align="center">
                There are no Fund-Investor Relationships assigned.
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <>
      <RenderFunds
        fundTagName={fundTagName}
        classes={classes}
        source="ids"
        resource="fundinvestors"
        fundInvestors={fundInvestors}
        mapFundInvSorted={mapFundInvSorted}
        numberOfTotalRows={numberOfTotalRows}
        hasEditPermission={hasEditPermission}
      />
    </>
  );
};

const FundInvestorTagSummary = props => {
  const {
    record,
    classes,
    fundInvestors,
    numberOfTotalRow,
    handleNewFundInv,
    hasEditPermission
  } = props;

  const [formErrors, setFormErrors] = useState({});

  const mapFundInvSorted = useMemo(() => {
    if (!fundInvestors.length) {
      return [];
    }
    const mapFundInvestors = fundInvestors.reduce((acc, fi) => {
      if (!acc[fi.fundId]) {
        acc[fi.fundId] = { fundName: fi.fundName, investors: [] };
      }
      acc[fi.fundId].investors.push({
        investorId: fi.investorId,
        investorName: fi.investorName
      });
      return acc;
    }, {});

    // sort by fund name
    const mapFundSorted = Object.entries(mapFundInvestors).sort((a, b) => {
      const [, value1] = a;
      const [, value2] = b;
      return value1.fundName
        .toLowerCase()
        .localeCompare(value2.fundName.toLowerCase());
    });

    // sort by investor name
    mapFundSorted.forEach(entry => {
      const [, val] = entry;
      val.investors.sort((a, b) =>
        a.investorName.toLowerCase().localeCompare(b.investorName.toLowerCase())
      );
    });

    return mapFundSorted;
  }, [fundInvestors]);

  const validateFundInvestorTagName = memoize(
    async (value, _allValues, fieldSate) => {
      if (record && value === record.name) {
        return;
      }
      if (fieldSate.active) {
        const fundInvestorTagNameValid = !!value.trim();
        let error;

        if (!fundInvestorTagNameValid) {
          error = `Fund Tag Name can't be empty space(s)`;
        } else {
          const fundInvestorTagNameExists = await FundInvestorTagApi.checkIfNameExists(
            value
          );
          if (fundInvestorTagNameExists) {
            error = 'Fund Investor Tag Name already exists';
          } else {
            error = undefined;
          }
        }
        setFormErrors({ ...formErrors, name: error });
        return error;
      }
      return formErrors.name;
    }
  );

  return (
    <Grid container spacing={2}>
      <div className={`${classes.containerFlex}`}>
        <Grid item xs={6}>
          <TextInput
            source="name"
            label="Tag Name"
            validate={[
              required(),
              isFieldContainsOnlySpaces,
              fieldLengthValidator(30),
              validateFundInvestorTagName
            ]}
            helperText={false}
            variant="standard"
            options={{ autoComplete: 'off' }}
          />
        </Grid>
        <Grid item xs={9} className={classes.checkBoxFlex}>
          <Field
            name="tagPrivacy"
            component={CheckBoxField}
            label="Private Tag"
            variant="standard"
            type="checkbox"
          />
        </Grid>

        <Grid item xs={9} className={classes.buttonFlex}>
          <AddFundsDialog
            key="addFund"
            fundInvestors={fundInvestors}
            handleNewFundInv={handleNewFundInv}
            hasEditPermission={hasEditPermission}
          />
        </Grid>
      </div>
      <Grid item xs={12}>
        <TextInput
          source="description"
          label="Description"
          validate={[isFieldContainsOnlySpaces, fieldLengthValidator(1000)]}
          options={{ autoComplete: 'off' }}
          variant="standard"
          helperText={false}
          multiline={true}
          rows="3"
        />
      </Grid>

      <Grid item xs={12} className={classes.gridLarge}>
        <RenderFundInvestors
          fundTagName={record.name}
          classes={classes}
          fundInvestors={fundInvestors}
          mapFundInvSorted={mapFundInvSorted}
          numberOfTotalRows={numberOfTotalRow}
          hasEditPermission={hasEditPermission}
        />
      </Grid>
    </Grid>
  );
};

export default withStyles(styles)(FundInvestorTagSummary);
